import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import type {ProyectoFormDTO} from '../../../api/Objetos';

import {normalizeProyectoForm} from './projectFormUtils';
import {ProjectFormLayout, type ProjectFormSectionNavItem} from './ProjectFormLayout';
import {clearProjectDraft, readProjectDraft, writeProjectDraft} from './projectDraft';
import {ProjectReadOnlyProvider, ProjectPublicViewProvider} from './projectForm.helpers';

import {InformacionGeneralSection} from './projectSections/InformacionGeneralSection';
import {RequerimientosSection} from './projectSections/RequerimientosSection';
import {AutorizacionesSection} from './projectSections/AutorizacionesSection';
import {DatosContratosSection} from './projectSections/DatosContratosSection';
import {DatosContablesSection} from './projectSections/DatosContablesSection';
import {SeguimientoPlataformaSection} from './projectSections/SeguimientoPlataformaSection';
import {SeguimientoCoffeSection} from './projectSections/SeguimientoCoffeSection';
import {CertificacionesDeObraSection} from './projectSections/CertificacionesDeObraSection';
import {HitosYObjetivosSection} from './projectSections/HitosYObjetivosSection';
import {ParticipacionSection} from './projectSections/ParticipacionSection';
import {InformesSection} from './projectSections/InformesSection';
import {ComunicacionSection} from './projectSections/ComunicacionSection';
import {ReunionesSection} from './projectSections/ReunionesSection';
import {PlazosSection} from './projectSections/PlazosSection';
import {AccionesSeguimientoSection} from './projectSections/AccionesSeguimientoSection';
import { useTranslation } from 'react-i18next';
import { slugify } from '../../shared/utils/slugify';

const getSections = (t: (key: string) => string): ProjectFormSectionNavItem[] => [
    {id: 'informacion-general', label: t('projectForm.sections.informacion-general')},
    {id: 'requerimientos', label: t('projectForm.sections.requerimientos')},
    {id: 'acciones-seguimiento', label: t('projectForm.sections.acciones-seguimiento')},
    {id: 'autorizaciones', label: t('projectForm.sections.autorizaciones')},
    {id: 'contratos', label: t('projectForm.sections.contratos')},
    {id: 'datos-contables', label: t('projectForm.sections.datos-contables')},
    {id: 'seguimiento-plataforma', label: t('projectForm.sections.seguimiento-plataforma')},
    {id: 'seguimiento-coffe', label: t('projectForm.sections.seguimiento-coffe')},
    {id: 'certificaciones', label: t('projectForm.sections.certificaciones')},
    {id: 'hitos', label: t('projectForm.sections.hitos')},
    {id: 'participacion', label: t('projectForm.sections.participacion')},
    {id: 'informes', label: t('projectForm.sections.informes')},
    {id: 'comunicacion', label: t('projectForm.sections.comunicacion')},
    {id: 'reuniones', label: t('projectForm.sections.reuniones')},
    {id: 'plazos', label: t('projectForm.sections.plazos')},
];

export function ProjectForm(props: {
    projectId?: number;
    initialValue?: ProyectoFormDTO | null;
    onSubmit: (value: ProyectoFormDTO) => Promise<void> | void;
    onCancel?: () => void;
    submitLabel: string;
    busy?: boolean;
    showIds?: boolean;
    draftKey: string;
    readOnly?: boolean;
}) {
    const { t } = useTranslation();
    const SECTIONS = getSections(t);
    const justSubmitted = useRef(false);
    const [showAllSections, setShowAllSections] = useState(false);

    const draftValue = useMemo(
        () => (props.readOnly ? null : readProjectDraft(props.draftKey)),
        [props.draftKey, props.readOnly]
    );

    const form = useForm<ProyectoFormDTO>({
        defaultValues: normalizeProyectoForm(draftValue ?? props.initialValue),
        shouldUnregister: false,
    });

    useEffect(() => {
        if (justSubmitted.current) {
            justSubmitted.current = false;
            return;
        }
        form.reset(normalizeProyectoForm(draftValue ?? props.initialValue));
    }, [form, draftValue, props.initialValue]);

    useEffect(() => {
        if (props.readOnly) return;
        let timeoutId: ReturnType<typeof setTimeout>;

        const subscription = form.watch((value) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                writeProjectDraft(props.draftKey, normalizeProyectoForm(value as ProyectoFormDTO));
            }, 2000);
        });

        return () => {
            clearTimeout(timeoutId);
            subscription?.unsubscribe?.();
        };
    }, [form, props.draftKey, props.readOnly]);

    const handleSubmit = form.handleSubmit(async (values: ProyectoFormDTO) => {
        justSubmitted.current = true;
        await props.onSubmit(values);
        clearProjectDraft(props.draftKey);
        form.reset(values);
    });

    const currentValue = form.getValues();
    const projectId = props.projectId || (currentValue as any).id || currentValue.informacionGeneral?.id || 0;
    const projectSlug = slugify(currentValue.informacionGeneral?.denominacion ?? '');
    const visibleSections = useMemo(() => {
        if (!props.readOnly || showAllSections) return SECTIONS;

        return SECTIONS.filter((section) => isSectionVisibleInReadOnly(section.id, currentValue));
    }, [currentValue, props.readOnly, showAllSections, SECTIONS]);

    return (
        <FormProvider {...form}>
            <ProjectPublicViewProvider value={Boolean(props.readOnly)}>
            <ProjectReadOnlyProvider value={Boolean(props.readOnly)}>
                <ProjectFormLayout
                    proyecto={currentValue as any}
                    sections={visibleSections}
                    onCancel={props.onCancel}
                    onSubmit={handleSubmit}
                    busy={props.busy}
                    submitLabel={props.submitLabel}
                    readOnly={props.readOnly}
                    onShowAll={() => setShowAllSections(true)}
                >
                    <form
                        className={`adminProjectForm${props.readOnly ? ' isReadOnly' : ''}`}
                        onSubmit={handleSubmit}
                    >
                        <fieldset style={{ display: 'contents', border: 'none', padding: 0, margin: 0 }}>
                            {isSectionVisibleInReadOnly('informacion-general', currentValue) && (
                                <InformacionGeneralSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('requerimientos', currentValue) && (
                                <RequerimientosSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {!props.readOnly && (
                                <AccionesSeguimientoSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('autorizaciones', currentValue) && (
                                <AutorizacionesSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('contratos', currentValue) && (
                                <DatosContratosSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('datos-contables', currentValue) && (
                                <DatosContablesSection proyectoId={projectId} projectSlug={projectSlug} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('seguimiento-plataforma', currentValue) && (
                                <SeguimientoPlataformaSection proyectoId={projectId} projectSlug={projectSlug} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('seguimiento-coffe', currentValue) && (
                                <SeguimientoCoffeSection proyectoId={projectId} projectSlug={projectSlug} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('certificaciones', currentValue) && (
                                <CertificacionesDeObraSection proyectoId={projectId} projectSlug={projectSlug} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('hitos', currentValue) && (
                                <HitosYObjetivosSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('participacion', currentValue) && (
                                <ParticipacionSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('informes', currentValue) && (
                                <InformesSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('comunicacion', currentValue) && (
                                <ComunicacionSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('reuniones', currentValue) && (
                                <ReunionesSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                            {isSectionVisibleInReadOnly('plazos', currentValue) && (
                                <PlazosSection proyectoId={projectId} showIds={Boolean(props.showIds)}/>
                            )}
                        </fieldset>
                    </form>
                </ProjectFormLayout>
            </ProjectReadOnlyProvider>
            </ProjectPublicViewProvider>
        </FormProvider>
    );
}

function isSectionVisibleInReadOnly(sectionId: string, value: ProyectoFormDTO) {
    switch (sectionId) {
        case 'informacion-general':
            return hasObjectContent(value.informacionGeneral);
        case 'requerimientos':
            return (value.requerimientos?.length ?? 0) > 0;
        case 'acciones-seguimiento':
            return false;
        case 'datos-contables':
            return value.datosContables?.publico === true || hasObjectContent(value.datosContables);
        case 'autorizaciones':
            return (value.autorizacionesSolicitadas?.length ?? 0) > 0;
        case 'contratos':
            return (
                value.datosContratos?.publico === true ||
                (value.datosContratos?.contratoMayores?.length ?? 0) > 0 ||
                (value.datosContratos?.contratoMenores?.length ?? 0) > 0 ||
                (value.datosContratos?.gastosImputadosFueraDeLaSubvencion?.length ?? 0) > 0
            );
        case 'seguimiento-plataforma':
            return value.seguimientoPlataforma?.publico === true || hasObjectContent(value.seguimientoPlataforma);
        case 'seguimiento-coffe':
            return value.seguimientoCoFFE?.publico === true || hasObjectContent(value.seguimientoCoFFE);
        case 'certificaciones':
            return value.certificacionesObra?.publico === true || hasObjectContent(value.certificacionesObra);
        case 'hitos':
            return (value.hitosYObjetivos?.length ?? 0) > 0;
        case 'participacion':
            return (value.participacionCiudadana?.length ?? 0) > 0;
        case 'informes':
            return (value.informesGestion?.length ?? 0) > 0;
        case 'comunicacion':
            return (value.comunicacionProyecto?.length ?? 0) > 0;
        case 'reuniones':
            return (value.reuniones?.length ?? 0) > 0;
        case 'plazos':
            return (value.plazosEjecucionJustificacion?.length ?? 0) > 0;
        default:
            return true;
    }
}

function hasObjectContent(value: Record<string, unknown> | undefined): boolean {
    if (!value) return false;

    return Object.entries(value).some(([key, entry]) => {
        if (key === 'id' || key === 'publico') return false;
        if (entry == null) return false;
        if (typeof entry === 'string') return entry.trim().length > 0;
        if (typeof entry === 'number') return entry !== 0;
        if (typeof entry === 'boolean') return entry;
        if (Array.isArray(entry)) return !isEmptyPrimitiveArray(entry);
        if (typeof entry === 'object') {
            if (isEmptyTableModel(entry as Record<string, unknown>)) return false;
            return hasObjectContent(entry as Record<string, unknown>);
        }
        return false;
    });
}

function isEmptyPrimitiveArray(entry: unknown[]): boolean {
    if (entry.length === 0) return true;

    return entry.every((item) => {
        if (item == null) return true;
        if (typeof item === 'string') return item.trim().length === 0;
        if (typeof item === 'number') return item === 0;
        if (typeof item === 'boolean') return item === false;
        return false;
    });
}

function isEmptyTableModel(value: Record<string, unknown>): boolean {
    if (!('columns' in value) || !('rows' in value) || !('cellStyles' in value)) {
        return false;
    }

    const columns = Array.isArray(value.columns) ? value.columns : [];
    const rows = Array.isArray(value.rows) ? value.rows : [];
    const cellStyles = value.cellStyles && typeof value.cellStyles === 'object'
        ? Object.keys(value.cellStyles as Record<string, unknown>)
        : [];

    const columnsEmpty = columns.length === 0 || columns.every((item) => String(item ?? '').trim().length === 0 || String(item) === '1');
    const rowsEmpty = rows.length === 0 || rows.every((row) =>
        Array.isArray(row) && row.every((cell) => String(cell ?? '').trim().length === 0)
    );

    return columnsEmpty && rowsEmpty && cellStyles.length === 0;
}
